import {
  AlertTriangle,
  Ban,
  CheckCircle2,
  CreditCard,
  Hourglass,
  Loader2,
  Lock,
  PauseCircle,
  ShieldOff,
  WifiOff,
  type LucideIcon,
} from "lucide-react";

import type { EngineReading } from "@/hooks/use-engine";
import { cn } from "@/lib/utils";
import type { EngineState } from "@/stores/engine";

export interface EngineStatusProps {
  engine: EngineReading;
  /** "chip" — a compact inline pill (top bar, tab label). "banner" — a
   * full-width strip (above a panel/list). */
  variant?: "chip" | "banner";
  className?: string;
}

type Tone = "neutral" | "busy" | "warn" | "danger" | "good";

interface StatusMeta {
  label: string;
  icon: LucideIcon;
  tone: Tone;
  spin?: boolean;
}

// `ready` intentionally maps to `null` — a healthy, idle engine renders
// nothing; showing a chip has to earn its place by needing the user's
// attention. Every other state describes what's blocking or busy.
const META: Record<EngineState, StatusMeta | null> = {
  ready: null,
  initializing: { label: "Connecting…", icon: Loader2, tone: "neutral", spin: true },
  unavailable: { label: "Backend unavailable", icon: WifiOff, tone: "danger" },
  processing: { label: "Working…", icon: Loader2, tone: "busy", spin: true },
  waiting: { label: "Queued…", icon: Hourglass, tone: "neutral" },
  partial: { label: "Partial result", icon: AlertTriangle, tone: "warn" },
  completed: { label: "Done", icon: CheckCircle2, tone: "good" },
  failed: { label: "Something failed", icon: Ban, tone: "danger" },
  needs_auth: { label: "Sign in required", icon: Lock, tone: "warn" },
  needs_billing: { label: "Upgrade required", icon: CreditCard, tone: "warn" },
  needs_permission: { label: "Access restricted", icon: ShieldOff, tone: "warn" },
  paused: { label: "Paused", icon: PauseCircle, tone: "neutral" },
};

// busy (primary/violet) and warn (amber) must be visually DISTINCT — a job in
// flight reads as brand-accent, a degraded/gated state reads as caution amber.
const TONE_CLASSES: Record<Tone, string> = {
  neutral: "border-border bg-muted text-muted-foreground",
  busy: "border-primary/30 bg-primary/10 text-primary",
  warn: "border-amber-400/40 bg-amber-400/10 text-amber-300",
  danger: "border-destructive/30 bg-destructive/15 text-destructive",
  good: "border-viridian/30 bg-viridian/10 text-viridian",
};

const RETRYABLE: EngineState[] = ["unavailable", "failed", "partial"];

/** Renders `useEngine()`'s current reading as a chip or banner — the ONE
 * status affordance for "is the engine busy / available / gated" anywhere in
 * the studio, so no surface grows its own bespoke spinner/pill for it. */
export function EngineStatus({ engine, variant = "chip", className }: EngineStatusProps) {
  const meta = META[engine.state];
  if (!meta) return null;
  const Icon = meta.icon;
  const showRetry = RETRYABLE.includes(engine.state);
  const role = meta.tone === "danger" ? "alert" : "status";

  if (variant === "banner") {
    return (
      <div
        role={role}
        className={cn(
          "flex items-center gap-2 rounded-md border px-3 py-2 text-[13px]",
          TONE_CLASSES[meta.tone],
          className,
        )}
      >
        <Icon className={cn("h-4 w-4 shrink-0", meta.spin && "animate-spin")} aria-hidden />
        <span className="flex-1">{meta.label}</span>
        {engine.activeJobs.length > 1 && (
          <span className="text-2xs text-muted-foreground">{engine.activeJobs.length} jobs</span>
        )}
        {showRetry && (
          <button
            type="button"
            onClick={engine.retry}
            className="rounded border border-current/30 px-2 py-0.5 text-2xs hover:bg-current/10"
          >
            Retry
          </button>
        )}
      </div>
    );
  }

  return (
    <span
      role={role}
      title={meta.label}
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-2xs font-medium",
        TONE_CLASSES[meta.tone],
        className,
      )}
    >
      <Icon className={cn("h-3 w-3 shrink-0", meta.spin && "animate-spin")} aria-hidden />
      {meta.label}
      {showRetry && (
        <button
          type="button"
          onClick={engine.retry}
          className="ml-0.5 underline decoration-dotted underline-offset-2 hover:text-foreground"
        >
          Retry
        </button>
      )}
    </span>
  );
}
